import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { GhostTrailerMatch } from 'api/yardReconciliation';
import {
  EnteredTrailer,
  compareGhostTrailerMatch,
  matchLocationLabel
} from './reconciliation';

/** Which answer is in flight, so only its button spins. */
export type GhostTrailerMatchAction = 'accept' | 'create';

const MATCH_RULE_LABELS: Record<GhostTrailerMatch['match'], string> = {
  exact: 'Same trailer number',
  digits: 'Same digits, different letters'
};

const matchOptionLabel = (match: GhostTrailerMatch) =>
  [match.scac, match.trailerNumber, matchLocationLabel(match)]
    .filter(Boolean)
    .join(' · ');

function matchIntro(count: number) {
  if (count === 1)
    return 'This campus already has a trailer that may be the one you entered.';
  return `This campus already has ${count} trailers that may be the one you entered. Pick the one to compare.`;
}

// Tailwind's reset strips a button's background and padding, so both answers
// set their own, as the page's other buttons do. `m: 0` drops DialogActions'
// margin between buttons; `gap` spaces them instead.
const ACTION_BUTTON_SX = { px: 2.5, py: 1, m: 0, whiteSpace: 'nowrap' };

const PRIMARY_BUTTON_SX = {
  ...ACTION_BUTTON_SX,
  'bgcolor': '#3b82f6',
  '&:hover': { bgcolor: '#2563eb' }
};

const SECONDARY_BUTTON_SX = {
  ...ACTION_BUTTON_SX,
  'color': '#2563eb',
  'border': '1px solid #3b82f6',
  '&:hover': { bgcolor: 'rgba(59,130,246,.06)', borderColor: '#2563eb' }
};

const MISMATCH_SX = { color: 'error.main', fontWeight: 600 };

/**
 * Shown when flux finds trailers on the campus that a ghost trailer answer may
 * mean. The person says one of them is the trailer (the exception resolves and
 * nothing moves), none is (the new trailer is created as entered), or backs out
 * with the close button (nothing changes).
 */
const GhostTrailerMatchDialog = ({
  matches,
  entered,
  busy,
  error,
  onAccept,
  onCreateNew,
  onCancel
}: {
  /** Open while non-empty. */
  matches: GhostTrailerMatch[];
  entered: EnteredTrailer | null;
  busy: GhostTrailerMatchAction | null;
  error: string | null;
  onAccept: (match: GhostTrailerMatch) => void;
  onCreateNew: () => void;
  onCancel: () => void;
}) => {
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  // A new set of matches starts on the first, the likeliest by flux's order.
  useEffect(() => {
    setSelectedUuid(matches[0]?.trailerUuid ?? null);
  }, [matches]);

  const selected = matches.find((m) => m.trailerUuid === selectedUuid) ?? matches[0];
  const rows = selected && entered ? compareGhostTrailerMatch(selected, entered) : [];
  const locationDiffers = rows.some((row) => row.label === 'Location' && !row.same);

  return (
    <Dialog
      open={matches.length > 0}
      onClose={busy ? undefined : onCancel}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}
      >
        Possible Match Found
        <IconButton
          aria-label="Close"
          onClick={onCancel}
          disabled={Boolean(busy)}
          edge="end"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {matchIntro(matches.length)}
        </Typography>

        {matches.length > 1 && (
          <RadioGroup
            value={selected?.trailerUuid ?? ''}
            onChange={(e) => setSelectedUuid(e.target.value)}
          >
            {matches.map((match) => (
              <FormControlLabel
                key={match.trailerUuid}
                value={match.trailerUuid}
                control={<Radio size="small" />}
                label={matchOptionLabel(match)}
                disabled={Boolean(busy)}
              />
            ))}
          </RadioGroup>
        )}

        {selected && (
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {MATCH_RULE_LABELS[selected.match]}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Matched trailer</TableCell>
                  <TableCell>You entered</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.label}</TableCell>
                    <TableCell>{row.matched}</TableCell>
                    <TableCell sx={row.same ? undefined : MISMATCH_SX}>
                      {row.entered}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {locationDiffers && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Choosing the matched trailer leaves it where it is: it is not moved to the
            location you entered.
          </Typography>
        )}

        {error && (
          <Box className="bg-red-50 border border-red-200 text-red-700 p-2 rounded">
            {error}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        <Button
          variant="outlined"
          sx={SECONDARY_BUTTON_SX}
          onClick={onCreateNew}
          disabled={Boolean(busy)}
        >
          {busy === 'create' ? 'Creating…' : 'Not a match, create new trailer'}
        </Button>
        <Button
          variant="contained"
          sx={PRIMARY_BUTTON_SX}
          onClick={() => selected && onAccept(selected)}
          disabled={Boolean(busy) || !selected}
        >
          {busy === 'accept' ? 'Resolving…' : 'Yes, this is the trailer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GhostTrailerMatchDialog;
